// Catalog.ID Client — Connected Modules Pane
// ============================================================================
//
// Lists "Allow always" grants the current identity has given to other dashboard
// modules — today the bitcash spending grants EFS and CPR ask for
// (`bitcash_wallet#…`, `bitcash-wallets-release`), plus whatever future
// siblings raise a consent dialog. Registered only in dashboard module
// context; never shown on the standalone catalog.id site.
//
// Each row is a (requesterId, keyId) with its grant date and an optional
// purpose string captured at grant time. Both verbs here are destructive —
// revoke a grant, forget a module identity — so each rides a button in its
// own row rather than the row click, which used to perform them (shell
// USAGE.md "Where a button belongs": one element inside the pane → the body).
//
// SINCE desktop-setup §6.2: this pane is the documented cure for a stale
// module registration, so it has to read like one. Every row names its module
// in the member's words with the service id beside it, the identity verb is
// "Forget" — the word the Registry's and Files' own copy uses when it sends
// people here — and a note above the list says what each verb costs.
//
// SINCE registration-wallets §7: a THIRD row kind, the bitcash spending tokens
// a module holds on one of this member's wallets (§4.12). It differs from the
// other two in a way the pane has to respect:
//
//   * it is not local. A grant and a module identity live in this browser's
//     localStorage; a token lives on the operator's Worker, is usable from any
//     machine the module runs on, and keeps working after "Forget this device".
//   * reading it costs the supersecret. The wallet key is bag-only and never
//     device-stored (D5), so the list is NOT loaded when the pane opens — it is
//     a deliberate act on the title row, and revoking is a second one.
//   * revoking it is the thing that actually cuts the module off. Revoking the
//     GRANT only means it has to ask again; the token it already holds keeps
//     spending until it is revoked here (or in Settings → Wallets).
// ============================================================================

import { createListPaneType, createElement, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneActionDescriptor, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import { PANE_IDS } from '../shell-config';
import { identity } from '../state/identity';
import { listAlways, revoke, type GrantPolicyEntry } from '../key-grant/grant-policy';
import { listAll as listRegisteredModules, unregister as unregisterModule, override as overrideModule } from '../key-grant/module-registry';
import { createRowActionColumn, type RowActionSpec } from '@catalog-shell';
import type { WorkerAuthToken } from '../key-grant/bitcash-worker-client';
import { createBitcashWorkerClient } from '../key-grant/bitcash-worker-client';
import { DEFAULT_TOKEN_LABEL, planWalletChoices } from '../key-grant/bitcash-handlers';
import type { BitcashWalletChoice } from '../key-grant/bitcash-types';
import {
    getAccountWalletKey, getOrgWalletKey, getOrgWalletOffer,
} from '../key-grant/bitcash-wallet-access';
import {
    defaultTokenSlot, orgDefaultTokenSlot, invalidateDefaultTokenCache,
} from '../key-grant/bitcash-token-cache';
import { moduleName, moduleNameWithId } from '../key-grant/module-names';

/**
 * Row union — the table mixes "Allow always" grants, registered module
 * identities and the spending tokens modules hold. The kind discriminator
 * selects the row's verb (revoke a grant vs. unregister a module vs. revoke a
 * token).
 */
export type ConnectedModuleRow =
    | { kind: 'grant'; entry: GrantPolicyEntry }
    | { kind: 'module'; moduleId: string; publicKeyB64: string; registeredAt: string; label?: string }
    | {
        kind: 'bitcash-token';
        /** `default` for the shared default token (unbound, Catalog.ID's scope), else the module id. */
        moduleId: string;
        /** Which wallet the token sits on — the id of a
         *  {@link BitcashWalletChoice}, so revoking can reopen the same one. */
        walletChoiceId: string;
        token: WorkerAuthToken;
    };

type Row = ConnectedModuleRow;

/** The `catalog:`-labelled, live tokens of one wallet, as rows. Pure.
 *
 *  Only OUR labels: a token the member pasted together in the wallet manager is
 *  theirs to manage there, and showing it under "Connected Modules" would claim
 *  a module holds it when nothing here knows that. Revoked tokens are dropped —
 *  they are audit records on the Worker, not a permission anyone still has. */
export function bitcashTokenRows(
    tokens: readonly WorkerAuthToken[], walletChoiceId: string,
): Extract<Row, { kind: 'bitcash-token' }>[] {
    const rows: Extract<Row, { kind: 'bitcash-token' }>[] = [];
    for (const token of tokens) {
        if (typeof token.label !== 'string' || !token.label.startsWith('catalog:')) continue;
        if (token.revokedAt) continue;
        rows.push({
            kind: 'bitcash-token',
            moduleId: token.label.slice('catalog:'.length),
            walletChoiceId,
            token,
        });
    }
    return rows;
}

/** What the Module column says for a token row. Pure. */
export function describeBitcashTokenModule(row: Extract<Row, { kind: 'bitcash-token' }>): string {
    return row.moduleId === 'default'
        ? 'All Catalog services (shared default token)'
        : moduleNameWithId(row.moduleId);
}

/** Enough of a row to name it — the confirmations hold less than a full
 *  {@link Row} and still have to say who they are about. */
export type NamedModuleRow =
    | { kind: 'grant'; entry: Pick<GrantPolicyEntry, 'requesterId' | 'label'> }
    | { kind: 'module'; moduleId: string; label?: string }
    | Extract<Row, { kind: 'bitcash-token' }>;

/**
 * The Module column, for any row kind (desktop-setup §6.2).
 *
 * The plain name first and the service id second, because the member arrives
 * here holding an instruction written by another module — "forget Registry's
 * registered identity" — and a row that says only `cpr` does not match it.
 * The id stays because it is what the module's own logs and refusals say.
 *
 * A module's own `label` ("Catalog Encrypted File Storage") is the fallback
 * for a module this dashboard does not know, never an override for one it
 * does.
 */
export function connectedModuleName(item: NamedModuleRow): string {
    if (item.kind === 'grant') {
        return moduleNameWithId(item.entry.requesterId, item.entry.label);
    }
    if (item.kind === 'module') return moduleNameWithId(item.moduleId, item.label);
    return describeBitcashTokenModule(item);
}

/**
 * The line above the list (desktop-setup §6.2). Both the Registry and Files
 * send members here for a stale or denied registration, and this pane used to
 * explain nothing: three row kinds, two verbs called "Revoke" and
 * "Unregister", and no statement of what either one costs.
 *
 * Exported so the words are a test rather than a screenshot.
 */
export const CONNECTED_MODULES_NOTE =
    'What the other Catalog modules may do with this account. Forget a module\'s '
    + 'registered identity and Catalog.ID stops trusting the copy of it installed on this '
    + 'computer: it can no longer use your keys, and the next time it connects it asks to '
    + 'register again — the same question you answered the first time. That is the cure '
    + 'when a module says Catalog.ID still trusts an earlier installation. The other verb '
    + 'here, Revoke, is smaller: the module stays registered and simply asks again the next '
    + 'time it needs that key. Revoke on a spending permission is the only act here that '
    + 'reaches beyond this computer: the module can no longer spend from that wallet from '
    + 'any computer.';

const columns: ColumnDef<Row>[] = [
    {
        key: 'kind',
        header: 'Type',
        width: 90,
        getValue: (item) => item.kind === 'grant' ? 'grant'
            : item.kind === 'module' ? 'identity' : 'token',
        type: { kind: 'text' },
    },
    {
        key: 'label',
        header: 'Module',
        width: 220,
        getValue: connectedModuleName,
        type: { kind: 'text' },
    },
    {
        key: 'keyId',
        header: 'Key',
        width: 180,
        getValue: (item) => item.kind === 'grant' ? item.entry.keyId
            : item.kind === 'module' ? '— (identity)'
                : item.token.authToken,
        type: { kind: 'text' },
    },
    {
        key: 'purpose',
        header: 'Purpose / pubkey',
        width: 280,
        getValue: (item) => item.kind === 'grant'
            ? (item.entry.purpose ?? '')
            : item.kind === 'module' ? (item.publicKeyB64.slice(0, 12) + '…')
                : describeTokenScope(item.token),
        type: { kind: 'text' },
    },
    {
        key: 'when',
        header: 'When',
        width: 140,
        sortable: true,
        getValue: (item) => item.kind === 'grant' ? item.entry.grantedAt
            : item.kind === 'module' ? item.registeredAt
                : new Date(item.token.createdAt || 0).toISOString(),
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
];

/** The caps and the expiry, in one cell: what the token may still spend. */
function describeTokenScope(token: WorkerAuthToken): string {
    const caps = `${token.dailyCap?.toLocaleString() ?? 'no cap'}/day · ${token.monthlyCap?.toLocaleString() ?? 'no cap'}/month BIT`;
    const expiry = token.expiresAt === null || token.expiresAt === undefined
        ? 'no expiry'
        : `until ${new Date(token.expiresAt).toLocaleDateString()}`;
    return `${caps} · ${expiry}`;
}

function rowKey(item: Row): string {
    if (item.kind === 'grant') return `grant::${item.entry.requesterId}::${item.entry.keyId}`;
    if (item.kind === 'module') return `module::${item.moduleId}`;
    return `token::${item.walletChoiceId}::${item.token.ref || item.token.authToken}`;
}

/**
 * Tokens loaded in this session, per identity, per wallet. NOT a cache of
 * anything durable: it is emptied on every reload, and a load costs one
 * supersecret prompt, which is what keeps the pane from asking for money-grade
 * credentials merely because someone opened a tab.
 */
const loadedTokens = new Map<string, Map<string, WorkerAuthToken[]>>();

function tokenRowsFor(cid: string): Row[] {
    const perWallet = loadedTokens.get(cid);
    if (!perWallet) return [];
    const rows: Row[] = [];
    for (const [walletChoiceId, tokens] of perWallet) {
        rows.push(...bitcashTokenRows(tokens, walletChoiceId));
    }
    return rows;
}

function createMixedDataSource(remember: (items: Row[]) => void): DataSource<Row> {
    return new DataSource<Row>({
        fetch: async (params): Promise<FetchResult<Row>> => {
            const cid = identity.getActive()?.cid;
            if (!cid) return { items: [], totalCount: 0 };
            const grants = listAlways(cid).map<Row>(entry => ({ kind: 'grant', entry }));
            const modules = listRegisteredModules(cid).map<Row>(m => ({
                kind: 'module',
                moduleId: m.moduleId,
                publicKeyB64: m.publicKeyB64,
                registeredAt: m.registeredAt,
                label: m.label,
            }));
            const all: Row[] = [...modules, ...grants, ...tokenRowsFor(cid)];
            const sliced = all.slice(params.offset, params.offset + params.limit);
            remember(sliced);
            return { items: sliced, totalCount: all.length };
        },
        keyFn: rowKey,
        pageSize: 100,
    });
}

/**
 * Pure: the row's verb. A grant is withdrawn ("Revoke", the module must ask
 * again); a module identity is forgotten ("Forget", the module must register
 * anew). Two different consequences, two labels.
 *
 * The verb is "Forget" and not "Unregister" because that is the word the
 * member was given: the Registry's and Files' own copy both say "open
 * Connected Modules and forget <module>'s registered identity" (§6.2). A
 * button under a different name makes the member guess that the two match.
 */
export function planConnectedModuleRowAction(item: Row): RowActionSpec | null {
    if (item.kind === 'grant') {
        return {
            label: 'Revoke',
            variant: 'danger',
            title: `Revoke "Allow always" for ${connectedModuleName(item)} → ${item.entry.keyId}`,
        };
    }
    if (item.kind === 'bitcash-token') {
        // Same word as a grant's, and deliberately: to a member both mean "this
        // module stops". The confirmation says which one it is.
        return {
            label: 'Revoke',
            variant: 'danger',
            title: `Revoke the spending permission ${describeBitcashTokenModule(item)} holds on this wallet`,
        };
    }
    return {
        label: 'Forget',
        variant: 'danger',
        title: `Forget the registered identity of ${connectedModuleName(item)}`
            + ' — it will ask to register again the next time it connects',
    };
}

/**
 * The confirmation for forgetting a module identity (desktop-setup §6.2).
 *
 * It used to quote the wire code the module would then receive
 * (`module-not-registered`) and call the traffic "Key Grant requests", which
 * told the member nothing about the two things they actually need to know:
 * that the module comes back and asks, and that nothing of theirs is deleted.
 * "Forget" is a frightening word next to a list of keys.
 */
export function describeForgetIdentity(
    item: { kind: 'module'; moduleId: string; label?: string },
): { title: string; message: string; confirmLabel: string } {
    const who = connectedModuleName(item);
    return {
        title: 'Forget this module\'s registered identity?',
        message: `Catalog.ID will forget the identity it holds for ${who}, and stop trusting `
            + 'the copy of it installed on this computer. That copy cannot use your keys until '
            + 'it registers again, and the next time it connects it will ask you to approve it '
            + '— the same question you answered the first time. Nothing else changes: your '
            + 'keys, your files and the permissions you have given stay as they are.',
        confirmLabel: 'Forget',
    };
}

/**
 * The column set is shared by every instance of this pane type, while the shell
 * to talk to belongs to the instance; the fetch that produced a row records it
 * against the row object — the only thing a cell click has in hand.
 */
const rowOrigin = new WeakMap<Row, PaneTypeRenderContext['shell']>();

const actionColumn = createRowActionColumn<Row>({
    plan: planConnectedModuleRowAction,
    execute: (item) => {
        const shell = rowOrigin.get(item);
        const cid = identity.getActive()?.cid;
        if (!shell || !cid) return;
        if (item.kind === 'grant') {
            void confirmAndRevoke(shell, cid, item.entry).then((revoked) => {
                if (revoked) {
                    shell.toast(
                        `Revoked ${moduleName(item.entry.requesterId, item.entry.label)} — ${item.entry.keyId}`,
                        'success');
                    shell.emit('data-changed', { type: 'key-grant-policy' });
                }
            });
        } else if (item.kind === 'bitcash-token') {
            void confirmAndRevokeToken(shell, cid, item).then((didChange) => {
                if (didChange) shell.emit('data-changed', { type: 'key-grant-policy' });
            });
        } else {
            void confirmAndUnregister(shell, cid, item).then((didChange) => {
                if (didChange) shell.emit('data-changed', { type: 'key-grant-policy' });
            });
        }
    },
});

const config: ListPaneConfig<Row> = {
    typeId: PANE_IDS.connectedModules,
    defaultTitle: 'Connected Modules',
    createDataSource: (ctx) => createMixedDataSource((items) => {
        for (const item of items) rowOrigin.set(item, ctx.shell);
    }),
    columns: [...columns, actionColumn],
    keyFn: rowKey,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    // No onItemSelect: a click on a row selects it and nothing more. The row's
    // verb is the button in the row itself.
    //
    // The title row carries the one verb that is about the PANE rather than a
    // row: loading the spending tokens, which costs a supersecret and a Worker
    // round-trip and therefore never happens on its own (§7).
    tabBarActions: (ctx, dataSource) => renderPaneActions(
        spendingTokenActions(ctx, dataSource as DataSource<Row>),
        { overflowTitle: 'Spending tokens' }),
    // The pane's own explanation (§6.2). A member sent here by another module
    // arrives with one instruction ("forget its registered identity") and three
    // row kinds in front of them; this says which verb is which and what each
    // one costs.
    createHeader: () => createElement('p', {
        className: 'cid-pane-note cid-pane-note--flush',
        textContent: CONNECTED_MODULES_NOTE,
        style: { margin: '0 0 8px 0', maxWidth: '78ch' },
    }),
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'key-grant-policy') {
                dataSource.refresh();
            }
        }));
    },
    ariaLabel: 'Connected modules',
};

// ── bitcash spending tokens ─────────────────────────────────────────────────

/**
 * The title-row verb. One action per wallet the member has — the registration
 * wallet always, the organization's when an owner or treasurer is active (D14)
 * — because "show me the tokens" is a question about ONE wallet: the key that
 * answers it is that wallet's, and no single prompt can open two.
 */
function spendingTokenActions(
    ctx: PaneTypeRenderContext, dataSource: DataSource<Row>,
): PaneActionDescriptor[] {
    // The title row is built synchronously and the wallet list is a blob read
    // (cid-encrypted-storage §7.2), so the pane offers the PROVISIONAL slot-0
    // row — exactly what it offered before the move, when a PIN-unlocked
    // session held no wallet entry at all. The action itself resolves the real
    // wallet when it runs. Listing every wallet here would mean re-rendering
    // the title row after an await, which this pane type has no seam for.
    const choices = planWalletChoices({ wallets: [], orgWallet: null });
    const actions: PaneActionDescriptor[] = choices.map(choice => ({
        key: `bitcash.tokens.${choice.id}`,
        label: choices.length > 1 ? `Spending tokens — ${choice.label}` : 'Spending tokens…',
        title: 'Ask your wallet which modules may spend from it.',
        overflow: true,
        execute: () => void loadSpendingTokens(ctx, dataSource, choice),
    }));
    // The organization's wallet is discovered asynchronously (its key wraps are
    // a network read), so it rides its own action rather than delaying the
    // title row until the answer is in.
    actions.push({
        key: 'bitcash.tokens.org',
        label: 'Spending tokens — organization wallet',
        title: 'For an owner or treasurer: the tokens issued on the organization\'s wallet.',
        overflow: true,
        execute: () => void loadOrgSpendingTokens(ctx, dataSource),
    });
    return actions;
}

async function loadSpendingTokens(
    ctx: PaneTypeRenderContext, dataSource: DataSource<Row>, choice: BitcashWalletChoice,
): Promise<void> {
    const cid = identity.getActive()?.cid;
    if (!cid) return;
    const walletKey = await getAccountWalletKey(choice);
    if (!walletKey) {
        ctx.shell.toast(
            'That wallet is not available — unlock your Catalog.ID and try again.', 'error');
        return;
    }
    await loadTokensInto(ctx, dataSource, cid, choice.id, walletKey);
}

async function loadOrgSpendingTokens(
    ctx: PaneTypeRenderContext, dataSource: DataSource<Row>,
): Promise<void> {
    const cid = identity.getActive()?.cid;
    if (!cid) return;
    const offer = await getOrgWalletOffer();
    if (!offer) {
        ctx.shell.toast('This account has no organization wallet.', 'info');
        return;
    }
    const walletKey = await getOrgWalletKey(offer.orgCid);
    if (!walletKey) {
        ctx.shell.toast('The organization wallet key is not available on this device.', 'error');
        return;
    }
    await loadTokensInto(ctx, dataSource, cid, `org:${offer.orgCid}`, walletKey);
}

async function loadTokensInto(
    ctx: PaneTypeRenderContext,
    dataSource: DataSource<Row>,
    cid: string,
    walletChoiceId: string,
    walletKey: string,
): Promise<void> {
    try {
        const tokens = await createBitcashWorkerClient().listAuthTokens(walletKey);
        const perWallet = loadedTokens.get(cid) ?? new Map<string, WorkerAuthToken[]>();
        perWallet.set(walletChoiceId, tokens);
        loadedTokens.set(cid, perWallet);
        dataSource.refresh();
        const shown = bitcashTokenRows(tokens, walletChoiceId).length;
        ctx.shell.toast(shown === 0
            ? 'No module holds a spending permission on this wallet.'
            : `${shown} spending permission${shown === 1 ? '' : 's'} on this wallet.`, 'info');
    } catch (err) {
        ctx.shell.toast(
            `The wallet's tokens could not be read: ${err instanceof Error ? err.message : 'unknown error'}`,
            'error');
    }
}

/**
 * Revoke one token. The supersecret is asked for AGAIN rather than held from
 * the listing: a spending key that lingers in a pane's closure for as long as a
 * tab is open is a worse trade than one extra prompt on a rare, destructive
 * act.
 *
 * The device cache of the default token is dropped on success (§4.12) — leaving
 * it would hand a revoked token to the next module that asks, which the Worker
 * refuses, costing the member a confusing failure and a second prompt.
 */
async function confirmAndRevokeToken(
    shell: import('@catalog-shell').ShellAPI,
    cid: string,
    item: Extract<Row, { kind: 'bitcash-token' }>,
): Promise<boolean> {
    const who = describeBitcashTokenModule(item);
    const ok = await shell.confirm({
        title: 'Revoke this spending permission?',
        message: `${who} will no longer be able to spend from this wallet. Anything it is `
            + 'part-way through paying for will fail. You can approve a new permission the next '
            + 'time it asks.',
        confirmLabel: 'Revoke',
        cancelLabel: 'Cancel',
        confirmVariant: 'no',
        requireExplicitChoice: true,
    });
    if (!ok) return false;

    const walletKey = await walletKeyForChoiceId(shell, item.walletChoiceId);
    if (!walletKey) return false;

    try {
        await createBitcashWorkerClient().revokeAuthToken(walletKey, item.token.authToken || item.token.ref || '');
    } catch (err) {
        shell.toast(
            `The permission could not be revoked: ${err instanceof Error ? err.message : 'unknown error'}`,
            'error');
        return false;
    }

    if (item.token.label === DEFAULT_TOKEN_LABEL) {
        const orgCid = item.walletChoiceId.startsWith('org:')
            ? item.walletChoiceId.slice('org:'.length) : null;
        await invalidateDefaultTokenCache(
            orgCid ? orgDefaultTokenSlot(cid, orgCid) : defaultTokenSlot(cid));
    }

    // Drop the revoked row from what this session is showing.
    const perWallet = loadedTokens.get(cid);
    const tokens = perWallet?.get(item.walletChoiceId);
    if (perWallet && tokens) {
        perWallet.set(item.walletChoiceId, tokens.filter(t => t.authToken !== item.token.authToken));
    }

    shell.toast(`Revoked the spending permission held by ${who}`, 'success');
    return true;
}

/** The wallet key behind a row, by the choice id the row was loaded under. */
async function walletKeyForChoiceId(
    shell: import('@catalog-shell').ShellAPI, walletChoiceId: string,
): Promise<string | null> {
    if (walletChoiceId.startsWith('org:')) {
        return await getOrgWalletKey(walletChoiceId.slice('org:'.length));
    }
    const keyIndex = Number(walletChoiceId.slice('account:'.length));
    const choice: BitcashWalletChoice = {
        id: walletChoiceId,
        kind: 'account',
        keyIndex: Number.isInteger(keyIndex) ? keyIndex : 0,
        label: '',
    };
    return await getAccountWalletKey(choice);
}

async function confirmAndRevoke(
    shell: import('@catalog-shell').ShellAPI,
    cid: string,
    item: GrantPolicyEntry,
): Promise<boolean> {
    const ok = await shell.confirm({
        title: 'Revoke access?',
        message: `Remove "Allow always" for ${moduleName(item.requesterId, item.label)} accessing `
            + `${item.keyId}? The module stays registered and will ask you again the next time `
            + 'it wants this key.',
        confirmLabel: 'Revoke',
        cancelLabel: 'Cancel',
        confirmVariant: 'no',
        requireExplicitChoice: true,
    });
    if (!ok) return false;
    revoke(cid, item.requesterId, item.keyId);
    return true;
}

/**
 * The module row's verb — forget. We don't offer an inline override here
 * (which would require a fresh pubkey from the module): if a module
 * re-registers under a new key, the user sees the mismatch in logs and can
 * forget this entry and re-launch the module.
 */
async function confirmAndUnregister(
    shell: import('@catalog-shell').ShellAPI,
    cid: string,
    item: { kind: 'module'; moduleId: string; label?: string },
): Promise<boolean> {
    const copy = describeForgetIdentity(item);
    const ok = await shell.confirm({
        title: copy.title,
        message: copy.message,
        confirmLabel: copy.confirmLabel,
        cancelLabel: 'Cancel',
        confirmVariant: 'no',
        requireExplicitChoice: true,
    });
    if (!ok) return false;
    unregisterModule(cid, item.moduleId);
    shell.toast(
        `${connectedModuleName(item)} will be asked to register again the next time it connects`,
        'success');
    return true;
}

// `overrideModule` is exported by the registry for future "user explicitly
// approves a re-registration" flows. Not yet wired into a UI affordance —
// see roadmap §6.1 item 5 ("re-register/override control") for the polish path.
void overrideModule;

export function createConnectedModulesPane() {
    return createListPaneType(config);
}

export { config as connectedModulesPaneConfig };

// Fallback so the pane still compiles if shell doesn't export a child type.
// Keeps downstream TS happy — no-op at runtime.
createElement;
